import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  BadgeCheck,
  Calendar,
  ChevronDown,
  ChevronUp,
  Database,
  Eye,
  FileText,
  GitBranch,
  Loader2,
  Mail,
  RefreshCw,
  Search,
  ShieldCheck,
  Sparkles,
  User,
  Users,
  X,
} from 'lucide-react';

import { platformPrincipalAPI } from '../services/api';
import PermissionGate from '../components/PermissionGate';

function cx(...classes) {
  return classes.filter(Boolean).join(' ');
}

function formatDate(value) {
  if (!value) return '—';

  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function displayValue(value, fallback = 'Not available') {
  if (value === null || value === undefined || value === '') return fallback;
  return value;
}

function truncateMiddle(value, start = 14, end = 10) {
  if (!value) return '—';

  const text = String(value);

  if (text.length <= start + end + 3) return text;

  return `${text.slice(0, start)}...${text.slice(-end)}`;
}

function getUserName(user) {
  return (
    user?.full_name ||
    user?.identity_preview?.full_name ||
    user?.identity_preview?.name ||
    user?.name ||
    user?.email ||
    user?.principal_uuid ||
    'Unknown user'
  );
}

function getUserEmail(user) {
  return user?.email || user?.identity_preview?.email || '';
}

function getEmployeeId(user) {
  return user?.employee_id || user?.identity_preview?.employee_id || '';
}

function getPrincipalUuid(user) {
  return user?.principal_uuid || user?.user_uuid || '';
}

function ConfidenceBadge({ value }) {
  const confidence = String(value || 'LOW').toUpperCase();

  const cls =
    confidence === 'HIGH'
      ? 'bg-emerald-50 text-emerald-700 ring-emerald-100'
      : confidence === 'MEDIUM'
        ? 'bg-orange-50 text-orange-700 ring-orange-100'
        : 'bg-slate-100 text-slate-700 ring-slate-200';

  return (
    <span className={`rounded-full px-2.5 py-1 text-xs font-bold ring-1 ${cls}`}>
      {confidence}
    </span>
  );
}

function TypeBadge({ value }) {
  const type = String(value || 'USER').replaceAll('_', ' ');

  return (
    <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-700 ring-1 ring-slate-200">
      {type}
    </span>
  );
}

function MetricTile({ title, value, subtitle, icon: Icon, tone = 'orange' }) {
  const toneClass =
    tone === 'emerald'
      ? 'bg-emerald-50 text-emerald-700 ring-emerald-100'
      : tone === 'amber'
        ? 'bg-amber-50 text-amber-700 ring-amber-100'
        : tone === 'red'
          ? 'bg-red-50 text-red-700 ring-red-100'
          : tone === 'slate'
            ? 'bg-slate-100 text-slate-700 ring-slate-200'
            : 'bg-orange-50 text-orange-700 ring-orange-100';

  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-slate-500">{title}</p>
          <p className="mt-2 text-3xl font-black text-slate-950">{value}</p>
          {subtitle && <p className="mt-1 text-xs text-slate-500">{subtitle}</p>}
        </div>

        <div className={cx('rounded-2xl p-3 ring-1', toneClass)}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </div>
  );
}

function OverviewPanel({ stats }) {
  return (
    <section className="rounded-[2rem] border border-orange-100 bg-gradient-to-br from-orange-50 via-white to-white p-5 shadow-sm">
      <div className="flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-bold text-orange-700 ring-1 ring-orange-100">
            <Sparkles className="h-3.5 w-3.5" />
            Platform user registry
          </div>

          <h2 className="mt-3 text-xl font-black text-slate-950">
            {stats.total} platform user{stats.total !== 1 ? 's' : ''}
          </h2>

          <p className="mt-1 max-w-2xl text-sm text-slate-500">
            A simple user-focused view across consent snapshots, PII records, employee IDs, identifiers and family relationships.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:min-w-[760px]">
          <MetricTile
            title="Users"
            value={stats.total}
            subtitle="Mapped profiles"
            icon={Users}
          />

          <MetricTile
            title="High Confidence"
            value={stats.high}
            subtitle="Strong identity match"
            icon={ShieldCheck}
            tone="emerald"
          />

          <MetricTile
            title="Employees"
            value={stats.employees}
            subtitle="Employee profiles"
            icon={BadgeCheck}
            tone="amber"
          />

          <MetricTile
            title="Family Members"
            value={stats.family}
            subtitle="Relationship links"
            icon={GitBranch}
            tone="slate"
          />
        </div>
      </div>
    </section>
  );
}

function UserIdentityBlock({ user }) {
  return (
    <div className="flex min-w-0 items-start gap-4">
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-orange-100 text-orange-700 ring-1 ring-orange-200">
        <User className="h-6 w-6" />
      </div>

      <div className="min-w-0">
        <h3 className="truncate text-base font-black text-slate-950">
          {displayValue(getUserName(user), 'Unknown user')}
        </h3>

        <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-600">
          <span className="inline-flex min-w-0 items-center gap-1.5">
            <Mail className="h-3.5 w-3.5 text-slate-400" />
            <span className="truncate">
              {displayValue(getUserEmail(user), 'Email unavailable')}
            </span>
          </span>

          <span>
            Employee ID:{' '}
            <span className="font-semibold text-slate-800">
              {displayValue(getEmployeeId(user), 'Unavailable')}
            </span>
          </span>
        </div>

        <p className="mt-2 font-mono text-xs text-slate-400">
          User UUID: {truncateMiddle(getPrincipalUuid(user), 22, 12)}
        </p>
      </div>
    </div>
  );
}

function UserCard({ user, isOpen, onView }) {
  return (
    <article
      className={cx(
        'rounded-[1.75rem] border bg-white p-5 shadow-sm transition',
        isOpen ? 'border-orange-300 ring-2 ring-orange-100' : 'border-slate-200 hover:border-orange-200 hover:shadow-md',
      )}
    >
      <div className="grid gap-5 xl:grid-cols-[1.4fr_0.9fr_0.9fr_auto] xl:items-start">
        <UserIdentityBlock user={user} />

        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-slate-400">
            User Type
          </p>

          <div className="mt-2 flex flex-wrap gap-2">
            <TypeBadge value={user.principal_type} />
            <ConfidenceBadge value={user.match_confidence} />
          </div>

          {user.match_reason && (
            <p className="mt-3 line-clamp-2 text-xs text-slate-500">
              {user.match_reason}
            </p>
          )}
        </div>

        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-slate-400">
            Linked Evidence
          </p>

          <div className="mt-2 flex flex-wrap gap-2">
            <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700 ring-1 ring-emerald-100">
              {user.active_records_count || 0} active
            </span>
            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-700 ring-1 ring-slate-200">
              {user.revoked_records_count || 0} revoked
            </span>
            <span className="rounded-full bg-amber-50 px-2.5 py-1 text-xs font-bold text-amber-700 ring-1 ring-amber-100">
              {user.retained_records_count || 0} retained
            </span>
            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-700 ring-1 ring-slate-200">
              {user.active_documents_count || 0} active docs
            </span>
            <span className="rounded-full bg-amber-50 px-2.5 py-1 text-xs font-bold text-amber-700 ring-1 ring-amber-100">
              {user.restricted_documents_count || 0} restricted docs
            </span>
          </div>

          <p className="mt-3 flex items-center gap-1.5 text-xs text-slate-500">
            <Calendar className="h-3.5 w-3.5" />
            Last seen: {formatDate(user.last_seen_at)}
          </p>
        </div>

        <div className="xl:text-right">
          <button
            onClick={() => onView(user)}
            className={cx(
              'inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold',
              isOpen
                ? 'border border-orange-200 bg-orange-50 text-orange-700 hover:bg-orange-100'
                : 'bg-orange-600 text-white hover:bg-orange-700',
            )}
          >
            <Eye className="h-4 w-4" />
            {isOpen ? 'Hide Details' : 'View Details'}
            {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </div>
      </div>
    </article>
  );
}

function SectionCard({ title, icon: Icon, description, children }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-start gap-3">
        {Icon && (
          <div className="rounded-xl bg-orange-50 p-2 text-orange-600 ring-1 ring-orange-100">
            <Icon className="h-4 w-4" />
          </div>
        )}

        <div>
          <h3 className="font-black text-slate-950">{title}</h3>
          {description && (
            <p className="mt-1 text-sm text-slate-500">{description}</p>
          )}
        </div>
      </div>

      {children}
    </section>
  );
}

function InfoRow({ label, value, mono = false }) {
  return (
    <div className="grid gap-1 border-b border-slate-100 py-2 last:border-b-0 sm:grid-cols-[180px_1fr]">
      <div className="text-xs font-bold uppercase tracking-wide text-slate-400">
        {label}
      </div>

      <div
        className={cx(
          'break-words text-sm text-slate-700',
          mono && 'font-mono text-xs',
        )}
      >
        {displayValue(value, '—')}
      </div>
    </div>
  );
}

function TabButton({ active, icon: Icon, label, onClick }) {
  return (
    <button
      onClick={onClick}
      className={cx(
        'inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-bold transition',
        active
          ? 'bg-orange-50 text-orange-700 ring-1 ring-orange-100'
          : 'text-slate-600 hover:bg-slate-50',
      )}
    >
      <Icon className="h-4 w-4" />
      {label}
    </button>
  );
}

function DetailMetricStrip({ detail }) {
  const user = detail?.principal || {};

  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
      <MetricTile
        title="PII Records"
        value={(detail?.pii_records || []).length}
        subtitle="Linked records"
        icon={Database}
        tone="amber"
      />

      <MetricTile
        title="Consents"
        value={(detail?.consent_snapshots || []).length}
        subtitle="Notice snapshots"
        icon={BadgeCheck}
        tone="emerald"
      />

      <MetricTile
        title="Family Links"
        value={(detail?.family_links || []).length}
        subtitle="Relationships"
        icon={GitBranch}
        tone="slate"
      />

      <MetricTile
        title="Identifiers"
        value={(user?.identifiers || []).length}
        subtitle="Mapped IDs"
        icon={User}
        tone="slate"
      />
    </div>
  );
}

function InlineUserProfile({
  selected,
  detail,
  detailLoading,
  activeTab,
  setActiveTab,
  onClose,
}) {
  const user = detail?.principal || selected || {};

  const tabs = [
    { id: 'overview', label: 'Overview', icon: User },
    { id: 'records', label: 'PII Records', icon: Database },
    { id: 'consents', label: 'Consent Notices', icon: BadgeCheck },
    { id: 'identifiers', label: 'Identifiers', icon: FileText },
    { id: 'family', label: 'Family Links', icon: GitBranch },
  ];

  return (
    <div className="rounded-[2rem] border border-orange-200 bg-orange-50/40 p-4 shadow-sm">
      {detailLoading ? (
        <div className="flex min-h-[280px] items-center justify-center rounded-3xl bg-white">
          <Loader2 className="h-7 w-7 animate-spin text-orange-600" />
        </div>
      ) : detail ? (
        <div className="space-y-5">
          <section className="rounded-[2rem] border border-orange-100 bg-gradient-to-br from-orange-50 via-white to-white p-5 shadow-sm">
            <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
              <UserIdentityBlock user={user} />

              <button
                onClick={onClose}
                className="inline-flex items-center gap-2 self-start rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-600 hover:bg-slate-50"
              >
                <X className="h-4 w-4" />
                Close details
              </button>
            </div>

            <div className="mt-5">
              <DetailMetricStrip detail={detail} />
            </div>
          </section>

          <section className="rounded-3xl border border-slate-200 bg-white shadow-sm">
            <div className="flex flex-wrap gap-2 border-b border-slate-200 p-3">
              {tabs.map((tab) => (
                <TabButton
                  key={tab.id}
                  active={activeTab === tab.id}
                  icon={tab.icon}
                  label={tab.label}
                  onClick={() => setActiveTab(tab.id)}
                />
              ))}
            </div>

            <div className="p-5">
              {activeTab === 'overview' && (
                <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
                  <SectionCard
                    title="User Details"
                    icon={User}
                    description="Primary user details for operational identification."
                  >
                    <InfoRow label="Full Name" value={user.full_name} />
                    <InfoRow label="Email" value={user.email} />
                    <InfoRow label="Employee ID" value={user.employee_id} />
                    <InfoRow label="User Type" value={user.principal_type} />
                    <InfoRow label="Department" value={user.department} />
                    <InfoRow label="Designation" value={user.designation} />
                    <InfoRow label="Location" value={user.location} />
                    <InfoRow label="Last Seen" value={formatDate(user.last_seen_at)} />
                  </SectionCard>

                  <SectionCard
                    title="Identity Mapping"
                    icon={ShieldCheck}
                    description="Technical mapping evidence is shown separately from user-facing identity."
                  >
                    <InfoRow label="Match Confidence" value={user.match_confidence} />
                    <InfoRow label="Match Reason" value={user.match_reason} />
                    <InfoRow label="Platform User UUID" value={user.principal_uuid} mono />
                    <InfoRow label="External User ID" value={user.external_user_id} mono />
                    <InfoRow label="Consent Platform User ID" value={user.consent_platform_user_id} mono />
                  </SectionCard>
                </div>
              )}

              {activeTab === 'records' && (
                <SectionCard
                  title="PII Records"
                  icon={Database}
                  description="PII records linked to this platform user. Raw PII values are not shown here."
                >
                  <div className="space-y-3">
                    {(detail.pii_records || []).map((record) => (
                      <div
                        key={record.uuid}
                        className="rounded-2xl border border-slate-200 bg-slate-50 p-4"
                      >
                        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                          <div>
                            <p className="font-mono text-sm font-semibold text-slate-950">
                              {truncateMiddle(record.uuid, 26, 14)}
                            </p>

                            <p className="mt-1 text-xs text-slate-500">
                              Consent: {record.consent_id || '—'} · Created: {formatDate(record.created_at)}
                            </p>

                            {record.privacy_notice_title && (
                              <p className="mt-2 text-sm font-semibold text-slate-800">
                                Notice: {record.privacy_notice_title}
                              </p>
                            )}
                          </div>

                          <div className="flex flex-wrap gap-2">
                            <span className="rounded-full bg-orange-50 px-2.5 py-1 text-xs font-bold text-orange-700 ring-1 ring-orange-100">
                              {record.documents_count || 0} documents · {String(record.processing_state || 'ACTIVE').replaceAll('_', ' ')}
                            </span>

                            {record.consent_context_available && (() => {
                              const consentState = String(record.revocation?.state || 'ACTIVE').toUpperCase();
                              const activeConsent = consentState === 'ACTIVE';
                              return (
                                <span className={`rounded-full px-2.5 py-1 text-xs font-bold ring-1 ${
                                  activeConsent
                                    ? 'bg-emerald-50 text-emerald-700 ring-emerald-100'
                                    : 'bg-amber-50 text-amber-800 ring-amber-200'
                                }`}>
                                  Consent {activeConsent ? 'active' : consentState.replaceAll('_', ' ').toLowerCase()}
                                </span>
                              );
                            })()}

                            {record.revocation?.available && (
                              <>
                                <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700 ring-1 ring-emerald-100">
                                  {record.revocation?.counts?.deleted || 0} deleted
                                </span>
                                <span className="rounded-full bg-amber-50 px-2.5 py-1 text-xs font-bold text-amber-800 ring-1 ring-amber-200">
                                  {record.revocation?.counts?.retained || 0} retained
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}

                    {(detail.pii_records || []).length === 0 && (
                      <p className="text-sm text-slate-500">No PII records linked.</p>
                    )}
                  </div>
                </SectionCard>
              )}

              {activeTab === 'consents' && (
                <SectionCard
                  title="Consent Notices"
                  icon={BadgeCheck}
                  description="Consent snapshots and privacy notices linked to this user."
                >
                  <div className="space-y-3">
                    {(detail.consent_snapshots || []).map((snapshot) => (
                      <div
                        key={snapshot.id || snapshot.consent_id}
                        className="rounded-2xl border border-slate-200 bg-slate-50 p-4"
                      >
                        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                          <div>
                            <p className="font-semibold text-slate-950">
                              {snapshot.notice_title || 'Notice title unavailable'}
                            </p>

                            <p className="mt-1 font-mono text-xs text-slate-500">
                              {truncateMiddle(snapshot.consent_id, 26, 14)}
                            </p>
                          </div>

                          <div className="text-left lg:text-right">
                            <span className={`rounded-full px-2.5 py-1 text-xs font-bold ring-1 ${
                              String(snapshot.status || '').toUpperCase() === 'ACTIVE'
                                ? 'bg-emerald-50 text-emerald-700 ring-emerald-100'
                                : 'bg-amber-50 text-amber-800 ring-amber-200'
                            }`}>
                              {String(snapshot.status || 'UNKNOWN').replaceAll('_', ' ')}
                            </span>

                            <p className="mt-2 text-xs text-slate-500">
                              Signed: {formatDate(snapshot.signed_at)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}

                    {(detail.consent_snapshots || []).length === 0 && (
                      <p className="text-sm text-slate-500">No consent notices linked.</p>
                    )}
                  </div>
                </SectionCard>
              )}

              {activeTab === 'identifiers' && (
                <SectionCard
                  title="Safe Identifiers"
                  icon={FileText}
                  description="Operational identifiers used for matching. Submitted sensitive PII is not exposed here."
                >
                  <div className="space-y-3">
                    {(user.identifiers || []).map((identifier) => (
                      <div
                        key={identifier.id}
                        className="rounded-2xl border border-slate-200 bg-slate-50 p-4"
                      >
                        <p className="font-semibold text-slate-950">
                          {String(identifier.identifier_type || 'Identifier').replaceAll('_', ' ')}
                        </p>

                        <p className="mt-1 break-all font-mono text-sm text-slate-600">
                          {identifier.value}
                        </p>

                        <p className="mt-2 text-xs text-slate-500">
                          Source: {identifier.source_system || '—'} · Confidence: {identifier.confidence || '—'}
                        </p>
                      </div>
                    ))}

                    {(user.identifiers || []).length === 0 && (
                      <p className="text-sm text-slate-500">No safe identifiers found.</p>
                    )}
                  </div>
                </SectionCard>
              )}

              {activeTab === 'family' && (
                <SectionCard
                  title="Family Links"
                  icon={GitBranch}
                  description="Relationship links connected to this user."
                >
                  <div className="space-y-3">
                    {(detail.family_links || []).map((link) => (
                      <div
                        key={link.id}
                        className="rounded-2xl border border-slate-200 bg-slate-50 p-4"
                      >
                        <p className="font-semibold text-slate-950">
                          {link.relationship_label || link.relationship_type || 'Relationship'}
                        </p>

                        <p className="mt-1 text-xs text-slate-500">
                          Employee: {truncateMiddle(link.employee_user_uuid, 16, 8)} · Member: {truncateMiddle(link.member_user_uuid, 16, 8)}
                        </p>
                      </div>
                    ))}

                    {(detail.family_links || []).length === 0 && (
                      <p className="text-sm text-slate-500">No family links found.</p>
                    )}
                  </div>
                </SectionCard>
              )}
            </div>
          </section>
        </div>
      ) : (
        <div className="rounded-3xl bg-white p-6 text-sm text-slate-500">
          User details are not available.
        </div>
      )}
    </div>
  );
}

export default function PlatformPrincipalsPage() {
  const [principals, setPrincipals] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const [search, setSearch] = useState('');
  const [principalType, setPrincipalType] = useState('');
  const [confidence, setConfidence] = useState('');

  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState('');

  const stats = useMemo(() => {
    return {
      total: principals.length,
      high: principals.filter((p) => p.match_confidence === 'HIGH').length,
      employees: principals.filter((p) => p.principal_type === 'EMPLOYEE').length,
      family: principals.filter((p) => p.principal_type === 'FAMILY_MEMBER').length,
    };
  }, [principals]);

  const loadPrincipals = async () => {
    setLoading(true);
    setError('');

    try {
      const res = await platformPrincipalAPI.list({
        search,
        principal_type: principalType,
        match_confidence: confidence,
      });

      setPrincipals(res.data?.results || []);
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          err.message ||
          'Failed to load platform users.',
      );
    } finally {
      setLoading(false);
    }
  };

  const openInlineDetail = async (principal) => {
    if (selected?.id === principal.id) {
      setSelected(null);
      setDetail(null);
      setActiveTab('overview');
      return;
    }

    setSelected(principal);
    setDetail(null);
    setActiveTab('overview');
    setDetailLoading(true);
    setError('');

    try {
      const res = await platformPrincipalAPI.detail(principal.id);
      setDetail(res.data);
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          err.message ||
          'Failed to load user detail.',
      );
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    loadPrincipals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PermissionGate permission="view_principals" showFallback>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex items-center gap-3">
            <div className="rounded-2xl bg-orange-100 p-3 text-orange-700 ring-1 ring-orange-200">
              <Users className="h-6 w-6" />
            </div>

            <div>
              <h1 className="text-2xl font-black text-slate-950">
                Platform Users
              </h1>
              <p className="mt-1 text-sm text-slate-500">
                Consolidated user profiles from consent snapshots, PII records, employee IDs and family links.
              </p>
            </div>
          </div>

          <button
            onClick={loadPrincipals}
            className="inline-flex items-center gap-2 rounded-xl bg-orange-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-orange-700"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>

        {error && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-red-700">
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-5 w-5" />
              <p className="text-sm font-bold">{error}</p>
            </div>
          </div>
        )}

        <OverviewPanel stats={stats} />

        <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-4">
            <h2 className="font-black text-slate-950">Find users</h2>
            <p className="mt-1 text-sm text-slate-500">
              Search by name, email, employee ID, external ID or user UUID.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-[1fr_220px_220px_auto]">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-slate-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, email, employee ID"
                className="w-full rounded-xl border border-slate-300 py-2.5 pl-9 pr-3 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
              />
            </div>

            <select
              value={principalType}
              onChange={(e) => setPrincipalType(e.target.value)}
              className="rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
            >
              <option value="">All User Types</option>
              <option value="PERSON">Person</option>
              <option value="EMPLOYEE">Employee</option>
              <option value="FAMILY_MEMBER">Family Member</option>
              <option value="UNKNOWN">Unknown</option>
            </select>

            <select
              value={confidence}
              onChange={(e) => setConfidence(e.target.value)}
              className="rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
            >
              <option value="">All Confidence</option>
              <option value="HIGH">High</option>
              <option value="MEDIUM">Medium</option>
              <option value="LOW">Low</option>
            </select>

            <button
              onClick={loadPrincipals}
              className="rounded-xl bg-orange-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-orange-700"
            >
              Search
            </button>
          </div>
        </div>

        <section className="rounded-[2rem] border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-5 flex items-center justify-between gap-3">
            <div>
              <h2 className="font-black text-slate-950">Users</h2>
              <p className="mt-1 text-sm text-slate-500">
                {principals.length} consolidated user profile{principals.length !== 1 ? 's' : ''} found.
              </p>
            </div>

            {loading && (
              <Loader2 className="h-5 w-5 animate-spin text-orange-600" />
            )}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-orange-600" />
            </div>
          ) : (
            <div className="space-y-4">
              {principals.map((principal) => (
                <React.Fragment key={principal.id}>
                  <UserCard
                    user={principal}
                    isOpen={selected?.id === principal.id}
                    onView={openInlineDetail}
                  />

                  {selected?.id === principal.id && (
                    <InlineUserProfile
                      selected={selected}
                      detail={detail}
                      detailLoading={detailLoading}
                      activeTab={activeTab}
                      setActiveTab={setActiveTab}
                      onClose={() => {
                        setSelected(null);
                        setDetail(null);
                        setActiveTab('overview');
                      }}
                    />
                  )}
                </React.Fragment>
              ))}

              {principals.length === 0 && (
                <div className="rounded-2xl border border-dashed border-slate-300 p-12 text-center">
                  <Search className="mx-auto h-12 w-12 text-slate-300" />
                  <p className="mt-3 text-lg font-bold text-slate-700">
                    No platform users found
                  </p>
                  <p className="mt-1 text-sm text-slate-500">
                    Try searching by name, email, employee ID or changing the filters.
                  </p>
                </div>
              )}
            </div>
          )}
        </section>
      </div>
    </PermissionGate>
  );
}